// AVOIR Phase 3 drawers (bodies + Close-left footers; the shell head is in shell/GoodwillCreateForm.jsx):
//  - GoodwillForm: 'Issue goodwill credit' = a voucher for a FUTURE order (amount, required reason, optional expiry). No money moves until it is applied as a discount.
//  - CompensationForm: 'Customer compensation' = cash/bank paid OUT to the customer as an EXPENSE. It does not change what the customer owes. Admin only.
// Both need the avoir_model_enabled switch ON (hidden at the door; the form still shows the reason if reached with it OFF) and an admin (the API refuses others).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import HelpIcon from '../../HelpIcon';
import { getFormatting } from '../../../services/formattingCache';
import { formatMoneyExact, TOKEN_TO_CHAR } from '../../../utils/format';
import { today } from '../../../shell/createFormUtils';
import { Emo, emoText } from '../../../shell/EmojiIcons';
import useActionLabel from '../../../i18n/useActionLabel';
import { pickableAccounts } from '../../../lib/resolveSupplierAccount';
import { normalizeAmount, newIdempotencyKey } from '../creditNoteHelpers.mjs';
import { validateGoodwill, validateCompensation } from './goodwillModel.mjs';
import SalesDocumentSheet from './SalesDocumentSheet';
import { AvoirQuickView } from './AvoirBits';
import { ProposePrint } from './DocPrint';
import { refusalText } from './avoirModel.mjs';
import useAvoirModel from './avoirModelStore';
import DrawerFooter from '../../../shell/drawer/DrawerFooter';
import { AccountPick } from '../../FormPickers';
import EnumSelect from '../../EnumSelect';
import { TextInput } from '../../inputs/Input';
import MoneyTextInput from '../../MoneyTextInput';
import useDocFieldRules, { DocRequiredNote } from '../../documents/useDocFieldRules';   // 47224 K-S10

const sepOf = () => { const f = getFormatting(); return { decimal: TOKEN_TO_CHAR[f.decimal_separator] ?? ',', thousands: TOKEN_TO_CHAR[f.thousands_separator] ?? ' ' }; };

// shared state of both forms: busy / message / idempotency key / dirty bookkeeping
function useSave({ onInteract, reset, call, okText, docOf }) {
  const { t, i18n } = useTranslation('avoir');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null); const [key, setKey] = useState(newIdempotencyKey);
  const touch = () => { setMsg(null); onInteract?.(); };
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await call(key);
      if (!res.ok) { setMsg({ type: 'err', text: refusalText(res, i18n.language) }); return; }
      setMsg({ type: 'ok', text: okText(res.data), doc: docOf ? docOf(res.data) : null }); reset(); setKey(newIdempotencyKey());
      window.dispatchEvent(new Event('sales-entry-posted')); window.dispatchEvent(new Event('avoir-credits-changed')); onInteract?.(false);
    } catch (e) { setMsg({ type: 'err', text: t('errors.generic') }); } finally { setBusy(false); }
  };
  return { busy, msg, touch, save };
}

const Banner = ({ on, isAdmin, partnerId, msg }) => {
  const { t } = useTranslation('avoir');
  const [sheet, setSheet] = useState(false);
  return (<>
    {on === false && <div className="warn" role="alert" data-testid="gw-off">{t('switchOff')}</div>}
    {on && !isAdmin && <div className="warn" role="alert" data-testid="gw-admin-only">{t('gw.adminOnly')}</div>}
    {!partnerId && <div className="warn" role="alert" data-testid="gw-no-customer">{t('gw.needCustomer')}</div>}
    {msg && msg.type === 'ok' && msg.doc
      ? <div data-testid="gw-msg"><ProposePrint doc={msg.doc} onOpen={() => setSheet(true)}>{emoText(msg.text)}</ProposePrint></div>
      : msg && <div className={msg.type === 'err' ? 'err-banner' : 'hint'} role={msg.type === 'err' ? 'alert' : 'status'} data-testid="gw-msg">{emoText(msg.text)}</div>}
    {sheet && msg?.doc && (msg.doc.kind === 'avoir_commercial' ? <AvoirQuickView id={msg.doc.id} onClose={() => setSheet(false)} /> : <SalesDocumentSheet kind={msg.doc.kind} id={msg.doc.id} onClose={() => setSheet(false)} />)}
  </>);
};

const Foot = ({ onClose, disabled, why, busy, onSave, label, testid }) => {   // a disabled primary names its reason (title + aria)
  const { t } = useTranslation('avoir');
  return (
    <DrawerFooter onClose={onClose} primary={<button type="button" className="btn success" disabled={disabled} aria-disabled={disabled} title={disabled && why ? why : undefined} aria-label={disabled && why ? `${label}: ${why}` : undefined} onClick={onSave} data-testid={testid}>{busy ? t('saving') : label}</button>} />
  );
};

export function GoodwillForm({ partnerId = '', partnerName = '', onClose, onInteract }) {
  const { t } = useTranslation('avoir'); const al = useActionLabel();
  const on = useAvoirModel(); const { isAdmin } = useAuth();
  const blank = { amount: '', notes: '', expires: '' };
  const [d, setD] = useState(blank);
  const sep = sepOf(); const amount = normalizeAmount(d.amount, sep);
  const error = validateGoodwill({ amount, notes: d.notes, expires: d.expires }, today());
  const { busy, msg, touch, save } = useSave({ onInteract, reset: () => setD(blank),
    call: (key) => api.createGoodwillCredit(partnerId, { amount, notes: d.notes.trim(), reason: d.notes.trim().slice(0, 120), expires_on: d.expires || null, idempotency_key: key }),
    okText: (r) => t('gw.saved', { no: (r.credit || r.avoir || r).avoir_no || '', amount: formatMoneyExact((r.credit || r.avoir || r).amount || amount) }),
    docOf: (r) => { const c = r.credit || r.avoir || r; return c.id ? { kind: 'avoir_commercial', id: c.id, no: c.avoir_no || '' } : null; } });
  const set = k => e => { setD(p => ({ ...p, [k]: e.target.value })); touch(); };
  const rules = useDocFieldRules('avoir_commercial', partnerId || null);
  const missing = rules.missing({ notes: d.notes, reason: d.notes, expires_on: d.expires });
  const blocked = busy || !!error || !partnerId || on !== true || !isAdmin || missing.length > 0;
  const blockedWhy = busy ? t('saving') : on !== true ? t('switchOff') : !isAdmin ? t('gw.adminOnly') : !partnerId ? t('gw.needCustomer') : error ? t('gw.errors.' + error) : rules.why(missing) || '';
  return (
    <>
      <div className="dr-body">
        <div className="hint">{t('gw.intro')}</div>
        <Banner on={on} isAdmin={isAdmin} partnerId={partnerId} msg={msg} />
        <div className="form">
          {partnerName && <div className="field full"><label>{t('gw.customer')}<span className="opt-lab">{t('locked')}</span></label><TextInput type="text" value={partnerName} disabled readOnly /></div>}
          <div className="field num"><label htmlFor="gw-amount">{t('colAmount')}<span className="req">*</span></label>
            <MoneyTextInput id="gw-amount" value={d.amount} onChange={set('amount')} placeholder="0" /></div>
          <div className="field"><label htmlFor="gw-exp">{t('gw.expiry')}{rules.req('expires_on') && <span className="req">*</span>}</label><TextInput id="gw-exp" type="date" min={today()} value={d.expires} onChange={set('expires')} />
            {error === 'expiry' && <span className="sub" role="alert">{t('gw.errors.expiry')}</span>}</div>
          <div className="field full"><label htmlFor="gw-notes">{t('gw.reason')}<span className="req">*</span></label>
            <textarea id="gw-notes" value={d.notes} onChange={set('notes')} placeholder={t('gw.reasonPlaceholder')} /></div>
        </div>
        <DocRequiredNote missing={missing} />
        <div className="hint"><Emo name="info" /> <span>{t('gw.noMoneyHint')}</span><HelpIcon helpKey="concept.goodwill_credit" /></div>
      </div>
      <Foot onClose={onClose} disabled={blocked} why={blockedWhy} busy={busy} onSave={save} label={al('issueGoodwill')} testid="gw-save" />
    </>
  );
}

export function CompensationForm({ partnerId = '', partnerName = '', onClose, onInteract }) {
  const { t } = useTranslation('avoir'); const al = useActionLabel();
  const on = useAvoirModel(); const { isAdmin } = useAuth();
  const blank = { amount: '', reason: '', accountId: '', date: today(), op: '', ref: '' };
  const [d, setD] = useState(blank);
  const [accounts, setAccounts] = useState([]); const [orders, setOrders] = useState([]);
  useEffect(() => {
    let live = true;
    Promise.resolve(api.getSelectableAccounts()).then(a => { if (live) setAccounts(pickableAccounts(Array.isArray(a) ? a : [], ['cash', 'bank'])); }).catch(() => {});
    if (partnerId) Promise.resolve(api.getSalesOrders({ partner_id: partnerId, page_size: 200, page: 1 })).then(r => { if (live) setOrders(r?.rows || []); }).catch(() => {});
    return () => { live = false; };
  }, [partnerId]);
  const sep = sepOf(); const amount = normalizeAmount(d.amount, sep);
  const error = validateCompensation({ amount, reason: d.reason, accountId: d.accountId, date: d.date }, today());
  const { busy, msg, touch, save } = useSave({ onInteract, reset: () => setD(blank),
    call: (key) => api.createCompensation(partnerId, { amount, reason: d.reason.trim(), account_from_id: d.accountId, date: d.date, reference_operation_id: d.op || null, idempotency_key: key }),
    okText: (r) => t('comp.saved', { no: (r.voucher || {}).voucher_no || '', amount: formatMoneyExact(amount) }),
    docOf: (r) => (r.voucher?.id ? { kind: 'payment_voucher', id: r.voucher.id, no: r.voucher.voucher_no || '' } : null) });
  const set = k => e => { setD(p => ({ ...p, [k]: e.target.value })); touch(); };
  // 47224: the payment voucher as this door writes it (post_customer_compensation stores no notes / signer), so a rule requiring them blocks here in the engine's words
  const rulesPv = useDocFieldRules('payment_voucher', null);
  const missingPv = rulesPv.missing({ notes: '', signed_by: '' });
  const blocked = busy || !!error || !partnerId || on !== true || !isAdmin || missingPv.length > 0;
  return (
    <>
      <div className="dr-body">
        <div className="hint">{t('comp.intro')}</div>
        <Banner on={on} isAdmin={isAdmin} partnerId={partnerId} msg={msg} />
        <div className="form">
          {partnerName && <div className="field full"><label>{t('gw.customer')}<span className="opt-lab">{t('locked')}</span></label><TextInput type="text" value={partnerName} disabled readOnly /></div>}
          <div className="field num"><label htmlFor="cp-amount">{t('colAmount')}<span className="req">*</span></label>
            <MoneyTextInput id="cp-amount" value={d.amount} onChange={set('amount')} placeholder="0" /></div>
          <div className="field"><label htmlFor="cp-date">{t('colDate')}<span className="req">*</span></label><TextInput id="cp-date" type="date" max={today()} value={d.date} onChange={set('date')} /></div>
          <div className="field full"><label htmlFor="cp-acct">{t('comp.account')}<span className="req">*</span></label>
            <AccountPick id="cp-acct" accounts={accounts} value={d.accountId} onChange={v => set('accountId')({ target: { value: v } })} placeholder={t('comp.accountPlaceholder')} /></div>
          <div className="field full"><label htmlFor="cp-op">{t('comp.order')}</label>
            <EnumSelect bare id="cp-op" value={d.op} onChange={v => set('op')({ target: { value: v } })}><option value="">{t('comp.orderNone')}</option>{orders.map(o => <option key={o.operation_id} value={o.operation_id}>{o.operation_id}</option>)}</EnumSelect></div>
          <div className="field full"><label htmlFor="cp-reason">{t('colReason')}<span className="req">*</span></label>
            <textarea id="cp-reason" value={d.reason} onChange={set('reason')} placeholder={t('comp.reasonPlaceholder')} /></div>
        </div>
        <DocRequiredNote missing={missingPv} />
        <div className="hint"><Emo name="info" /> <span>{t('comp.expenseHint')}</span><HelpIcon helpKey="concept.customer_compensation" /></div>
      </div>
      <Foot onClose={onClose} disabled={blocked} why={rulesPv.why(missingPv)} busy={busy} onSave={save} label={al('recordCompensation')} testid="comp-save" />
    </>
  );
}
